import React from 'react';
import Icon from 'icons';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'store/hooks';
import Input from 'components/input';
import Button from 'components/button';
import request from 'request';
import styles from './result-box.module.scss';
import prettierStatus from 'utils/prettier-status';

const ResultBox = () => {
	const dispatch = useDispatch();
	const contextApplication = useSelector((state) => state.application);
	const [decision, setDecision] = React.useState({
		key: '',
		value: '',
	});
	const [commentary, setCommentary] = React.useState('');
	const [pending, setPending] = React.useState(false);

	const setFinalDecision = () => {
		setPending(true);
		request
			.put(`/applications/${contextApplication.data?.id}/review`, {
				decision: decision.key,
				commentary: commentary,
			})
			.then(() => dispatch.application.FETCH_APPLICATION_BY_ID({ id: contextApplication.data?.id }))
			.finally(() => setPending(false));
	};

	return (
		<div className={styles.resultList}>
			{contextApplication.data?.expert_decision !== null && (
				<div className={classNames(styles.resultBox, styles.finalDecision)}>
					<div className={styles.resultBox_title}>Final decision</div>
					<div className={styles.finalResult}>
						<Icon
							size={120}
							name={
								contextApplication.data?.expert_decision === 'approved'
									? 'tickCircle'
									: 'closeFilled'
							}
							className={classNames(
								contextApplication.data?.expert_decision === 'approved' ? styles.green : styles.red,
							)}
						/>
						<div
							className={classNames(
								styles.finalResultText,
								contextApplication.data?.expert_decision === 'approved' ? styles.green : styles.red,
							)}
						>
							{prettierStatus(contextApplication.data?.expert_decision)}
						</div>
						<div className={styles.finalResultDescription}>
							{contextApplication.data?.expert_decision_description}
						</div>
					</div>
				</div>
			)}
			<div className={styles.resultBox}>
				<div className={styles.resultBox_title}>
					Application status (ID {contextApplication.data?.id})
				</div>
				{contextApplication.data?.is_approved !== null && (
					<div className={styles.finalResult}>
						<Icon
							size={120}
							name={
								contextApplication.data?.is_approved === 'approved' ? 'tickCircle' : 'closeFilled'
							}
							className={classNames({
								[styles.green]: contextApplication.data?.is_approved === 'approved',
								[styles.red]: contextApplication.data?.is_approved === 'rejected',
								[styles.yellow]:
									contextApplication.data?.is_approved !== 'approved' &&
									contextApplication.data?.is_approved !== 'rejected',
							})}
						/>
						<div
							className={classNames(
								styles.finalResultText,
								contextApplication.data?.is_approved === 'approved'
									? styles.green
									: contextApplication.data?.is_approved === 'rejected'
										? styles.red
										: styles.yellow,
							)}
						>
							{prettierStatus(contextApplication.data?.is_approved)}
						</div>
					</div>
				)}
				<div className={styles.resultBox_resultText}>
					<div className={styles.item}>
						<div className={styles.key}>OCR:</div>
						<div
							className={classNames(styles.value, {
								[styles.grey]: !contextApplication.data?.ocr_log?.is_approved,
								[styles.green]: contextApplication.data?.ocr_log?.is_approved === 'approved',
								[styles.yellow]: contextApplication.data?.ocr_log?.is_approved === 'review',
								[styles.red]: contextApplication.data?.ocr_log?.is_approved === 'rejected',
							})}
						>
							{prettierStatus(contextApplication.data?.ocr_log?.is_approved) ?? 'No decision'}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.key}>Scoring:</div>
						<div
							className={classNames(styles.value, {
								[styles.red]: contextApplication.data?.scoring_log?.is_approved === 'rejected',
								[styles.green]: contextApplication.data?.scoring_log?.is_approved === 'approved',
								[styles.yellow]: contextApplication.data?.scoring_log?.is_approved === 'review',
								[styles.grey]: !contextApplication.data?.scoring_log?.is_approved,
							})}
						>
							{contextApplication.manual_factors?.is_approved &&
							!contextApplication.data?.scoring_log
								? 'N/A'
								: contextApplication.data?.scoring_log?.is_approved
									? prettierStatus(contextApplication.data.scoring_log.is_approved) +
										(contextApplication.data?.scoring_log.prediction
											? ` (${contextApplication.data?.scoring_log.prediction}%)`
											: '')
									: 'No decision'}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.key}>Manual stop factors:</div>
						<div
							className={classNames(styles.value, {
								[styles.grey]: !contextApplication.manual_factors?.is_approved,
								[styles.green]: contextApplication.manual_factors?.is_approved === 'approved',
								[styles.red]: contextApplication.manual_factors?.is_approved === 'rejected',
								[styles.yellow]: contextApplication.manual_factors?.is_approved === 'review',
							})}
						>
							{prettierStatus(contextApplication.manual_factors?.is_approved) ?? 'No decision'}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.key}>Risky legal case:</div>
						<div
							className={classNames(styles.value, {
								[styles.grey]: !contextApplication.data?.main_legal_case_status,
								[styles.green]: contextApplication.data?.main_legal_case_status === 'approved',
								[styles.yellow]: contextApplication.data?.main_legal_case_status === 'review',
								[styles.red]: contextApplication.data?.main_legal_case_status === 'rejected',
							})}
						>
							{contextApplication.data?.main_legal_case_status
								? prettierStatus(contextApplication.data?.main_legal_case_status)
								: 'No decision'}
						</div>
					</div>
					<div className={styles.item}>
						<div className={styles.key}>Early Renewal Eligibility:</div>
						<div
							className={classNames(styles.value, {
								[styles.grey]:
									(contextApplication.data?.early_renewal_eligibility as string) === 'N/A' ||
									!contextApplication.data?.early_renewal_eligibility,
								[styles.green]: contextApplication.data?.early_renewal_eligibility === 'approved',
								[styles.yellow]: contextApplication.data?.early_renewal_eligibility === 'review',
								[styles.red]: contextApplication.data?.early_renewal_eligibility === 'rejected',
							})}
						>
							{contextApplication.data?.early_renewal_eligibility
								? prettierStatus(contextApplication.data?.early_renewal_eligibility) +
									(contextApplication.data?.early_renewal_eligibility_percentage
										? ` (${
												Number.isInteger(
													contextApplication.data?.early_renewal_eligibility_percentage * 100,
												)
													? contextApplication.data?.early_renewal_eligibility_percentage * 100
													: (
															contextApplication.data?.early_renewal_eligibility_percentage * 100
														).toFixed(1)
											}%)`
										: '')
								: 'No decision'}
						</div>
					</div>
				</div>
			</div>
			{contextApplication.data?.is_approved === 'review' &&
				contextApplication.data?.expert_decision === null && (
					<div className={classNames(styles.resultBox, styles.finalDecision)}>
						<div className={styles.resultBox_title}>Final decision</div>
						<div className={styles.resultBox_resultText}>
							<Input
								value={decision.key}
								id="decision"
								title="Decision"
								placeholder="Select final decision"
								type="select"
								onSelect={(option) => setDecision(option)}
								options={[
									{
										key: 'approved',
										value: 'Approve',
									},
									{
										key: 'rejected',
										value: 'Reject',
									},
								]}
							/>
							<Input
								id="commentary"
								title="Comment"
								value={commentary}
								type="textarea"
								onInput={(value) => setCommentary(value)}
							/>
							{decision.key.trim() !== '' && commentary.trim() !== '' && (
								<Button color="violet" pending={pending} onClick={setFinalDecision}>
									Submit
								</Button>
							)}
						</div>
					</div>
				)}
		</div>
	);
};

export default ResultBox;
